import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { ScanProgress, ScanResults } from '../components/scanner';
import { useScanStatus } from '../hooks/useScanner';
import { Layout } from '../components/layout';

export const ScanResultPage: React.FC = () => {
  const { scanId } = useParams<{ scanId: string }>();
  const { data: scanStatus, isLoading, error } = useScanStatus(scanId || null);

  return (
    <Layout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'var(--space-8) var(--space-4)' }}>
        {/* Back button */}
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <Link
            to="/scanner"
            className="btn btn-ghost btn-sm"
            style={{ display: 'inline-flex', textDecoration: 'none' }}
          >
            <ArrowLeft size={16} />
            New Scan
          </Link>
        </div>

        {/* Loading state */}
        {isLoading && (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: 'var(--space-12)', color: 'var(--color-muted)' }}>
              Loading scan...
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
              <AlertCircle size={48} style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }} />
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-xl)',
                fontWeight: 700,
                textTransform: 'uppercase',
                marginBottom: 'var(--space-2)',
              }}>
                Error Loading Scan
              </h2>
              <p style={{ color: 'var(--color-muted)', marginBottom: 'var(--space-4)' }}>
                {error instanceof Error ? error.message : 'Failed to load scan'}
              </p>
              <Link to="/scanner" className="btn btn-primary btn-md" style={{ textDecoration: 'none' }}>
                Start New Scan
              </Link>
            </div>
          </div>
        )}

        {/* Scan in progress */}
        {scanStatus && (scanStatus.status === 'queued' || scanStatus.status === 'running') && (
          <ScanProgress progress={scanStatus.progress} url={scanId || ''} />
        )}

        {/* Scan failed */}
        {scanStatus && scanStatus.status === 'failed' && (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
              <AlertCircle size={48} style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }} />
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--text-xl)',
                fontWeight: 700,
                textTransform: 'uppercase',
                marginBottom: 'var(--space-2)',
              }}>
                Scan Failed
              </h2>
              <p style={{ color: 'var(--color-muted)', marginBottom: 'var(--space-4)' }}>
                {scanStatus.error || 'An error occurred during the scan'}
              </p>
              <Link to="/scanner" className="btn btn-primary btn-md" style={{ textDecoration: 'none' }}>
                Try Again
              </Link>
            </div>
          </div>
        )}

        {/* Scan completed */}
        {scanStatus && scanStatus.status === 'completed' && scanStatus.result && (
          <ScanResults result={scanStatus.result} scanId={scanId || ''} />
        )}
      </div>
    </Layout>
  );
};

export default ScanResultPage;
